import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';

const DownloadIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className="w-5 h-5"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const YoutubeIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className="w-5 h-5"
  >
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const DownloadCenter: React.FC = () => {
  const bankingForms = [
    { name: 'MyBank account closure form', path: '/assets/downloads/MyBank-account-closure-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank account to account transfer form', path: '/assets/downloads/MyBank-account-to-account-transfer-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank agent introduction form', path: '/assets/downloads/MyBank-agent-introduction-form-A4_INTERACTIVE.pdf' },
    { name: 'MyBank card application form', path: '/assets/downloads/MyBank-card-application-form-A4_INTERACTIVE.pdf' },
    { name: 'MyBank card cancellation request form', path: '/assets/downloads/MyBank-card-cancellation-request-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank card collection request form', path: '/assets/downloads/MyBank-card-collection-request-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank card limit increase-decrease request form', path: '/assets/downloads/MyBank-card-limit-increase-decrease-request-form-A4_INTERACTIVE-.pdf' },
    { name: 'MyBank cash deposit form (Somali & English)', path: '/assets/downloads/MyBank-cash-deposit-form-Somali-english-A5_INTERACTIVE.pdf' },
    { name: 'MyBank cash withdrawal form', path: '/assets/downloads/MyBank-cash-withdrawal-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank cheque book request form', path: '/assets/downloads/MyBank-cheque-book-request-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank data change request form', path: '/assets/downloads/MyBank-data-change-request-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank email instructions authority and indemnity form', path: '/assets/downloads/MyBank-email-instructions-authority-and-indemnity-form-A4_INTERACTIVE.pdf' },
    { name: 'MyBank house cheque deposit form', path: '/assets/downloads/MyBank-house-cheque-deposit-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank internal operations memo', path: '/assets/downloads/MyBank-internal-operations-memo-A5_INTERACTIVE.pdf' },
    { name: 'MyBank personal account opening form – ENGLISH', path: '/assets/downloads/MyBank-personal-account-opening-form-ENGLISH-A4_INTERACTIVE.pdf' },
    { name: 'MyBank personal account opening form – SOMALI', path: '/assets/downloads/MyBank-personal-account-opening-form-SOMALI-A4_INTERACTIVE-2.pdf' },
    { name: 'MyBank signature change form', path: '/assets/downloads/MyBank-signature-change-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank specimen signature card', path: '/assets/downloads/MyBank-specimen-signature-card-A5_INTERACTIVE.pdf' },
    { name: 'MyBank standing order form', path: '/assets/downloads/MyBank-standing-order-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank statement request form', path: '/assets/downloads/MyBank-statement-request-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank stop payment instruction form', path: '/assets/downloads/MyBank-stop-payment-instruction-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank swift application form', path: '/assets/downloads/MyBank-swift-application-form-A4_INTERACTIVE.pdf' },
    { name: 'MyBank treasury in form', path: '/assets/downloads/MyBank-treasury-in-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank treasury out form', path: '/assets/downloads/MyBank-treasury-out-form-A5_INTERACTIVE.pdf' },
    { name: 'MyBank business account opening form – ENGLISH', path: '/assets/downloads/MyBank-business-account-opening-form-ENGLISH-A4_INTERACTIVE.pdf' },
    { name: 'MyBank business account opening form – SOMALI', path: '/assets/downloads/MyBank-business-account-openning-form-SOMALI-A4_INTERACTIVE.pdf' },
  ];

  return (
    <>
      <SEO
        title="MyBank - Download Center"
        description="Download MyBank forms, guides and access mobile banking resources"
        keywords={[
          'banking forms',
          'mobile banking',
          'document download',
          'MyBank forms',
          'banking forms',
          'mobile banking',
          'document download',
          'MyBank forms',
          'banking forms',
          'mobile banking',
          'document download',
          'MyBank forms',
          'banking forms',
          'mobile banking',
          'document download',
          'MyBank forms',
        ]}
      />

      {/* Hero Section */}
      <div className="relative bg-[#001F5F] overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#017EFF]/20 to-transparent" />
          <div className="absolute right-0 top-0 -translate-y-1/4 translate-x-1/4 w-96 h-96 rounded-full bg-[#017EFF]/10 blur-3xl" />
          <div className="absolute left-0 bottom-0 translate-y-1/4 -translate-x-1/4 w-96 h-96 rounded-full bg-[#017EFF]/10 blur-3xl" />
        </div>

        <div className="relative container mx-auto px-4 py-20">
          {/* Breadcrumb */}
       

          {/* Hero Content */}
          <div className="max-w-3xl mx-auto text-center my-6">
            <h1 className="text-5xl font-bold text-white mb-6">
              Download Center
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Access all MyBank forms, guides and resources in one convenient location. Everything you need, just a click away.
            </p>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-white/20">
              <svg 
                className="w-5 h-5 text-[#017EFF]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-white/90">
                All forms are available in both English and Somali
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Mobile Banking Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-[#001F5F] my-6">MyBank Mobile Banking</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-lg p-6 shadow-2xl">
              <h3 className="text-xl font-semibold mb-4">Mobile Banking Guide</h3>
              <div className="aspect-video mb-4">
                <iframe
                  className="w-full h-full rounded-lg"
                  src="https://www.youtube.com/embed/srVcUbq38vw"
                  title="MyBank Mobile Banking Guide"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="flex items-center gap-4">
                <a
                  href="/assets/downloads/MyBank-Mobile-Banking-app-2021.mp4"
                  download="MyBank-Mobile-Banking-Guide.mp4"
                  className="flex items-center gap-2 bg-[#017EFF] text-white px-4 py-2 rounded hover:bg-[#0056B3] transition-colors"
                >
                  <DownloadIcon />
                  Download Guide
                </a>
                <a
                  href="https://www.youtube.com/watch?v=srVcUbq38vw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                >
                  <YoutubeIcon />
                  Watch on YouTube
                </a>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 shadow-2xl">
              <h3 className="text-xl font-semibold mb-4">Download Mobile App</h3>
              <p className="text-gray-600 mb-4">
                Get the MyBank mobile banking app for secure and convenient banking on the go.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-white"
          >
           
            <div className="flex flex-wrap gap-4">
              <motion.a
                href="https://apps.apple.com/za/app/mybank-mobile-banking/id1560331953"
                target="_blank"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex items-center space-x-2 bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-900 transition-colors duration-300"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div>
                  <div className="text-xs">Download on the</div>
                  <div className="text-xl font-semibold">App Store</div>
                </div>
              </motion.a>
   
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.a
                href="https://play.google.com/store/apps/details?id=com.mode.mybank&hl=en&pli=1"
                target="_blank"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex items-center space-x-2 bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-900 transition-colors duration-300"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <div>
                  <div className="text-xs">GET IT ON</div>
                  <div className="text-xl font-semibold">Google Play</div>
                </div>
              </motion.a>
        </div>
            </div>
          </div>
        </section>

        {/* Forms Section */}
        <section>
          <h2 className="text-2xl font-bold text-[#001F5F] mb-6">Download Bank Related Docs</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 my-6">
            {bankingForms.map((form, index) => (
              <a
                key={index}
                href={form.path}
                download={form.name + '.pdf'}
                className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors group shadow-lg"
              >
                <DownloadIcon />
                <span className="text-gray-700 group-hover:text-[#017EFF]">{form.name}</span>
              </a>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default DownloadCenter;
